<template>
  <component :is="layoutComponent" v-bind="layoutProps">
    <Alert :i18nData="asyncError" />
    <Spinner :status="loading" />
    <div v-show="!loading" class="nl-form">
      <SegmentedTabs v-if="!isRiv" v-model="loginType" :options="tabs" :aria-label="$t('pages.newLogin.tabs.groupLabel')" class="nl-form__tabs" />

      <b-form class="nl-form__body" @submit.prevent="onSubmit">
        <!-- identifier (phone / email / customer code) -->
        <div class="nl-field">
          <label class="nl-field__label" for="nl-login">{{ currentLabel }}</label>
          <div v-if="isPhoneForm" class="nl-field__phone">
            <button
              ref="prefixButton"
              type="button"
              class="nl-prefix"
              aria-haspopup="listbox"
              :aria-expanded="pickerOpen ? 'true' : 'false'"
              aria-controls="nl-prefix-panel"
              :aria-label="$t('pages.newLogin.prefixPicker.triggerLabel', {
                country: currentCountryPrefix.name,
                prefix: currentCountryPrefix.prefix
              })"
              @click="openPrefixPicker"
            >
              <b-img
                :src="`/img/flags/${currentCountryPrefix.code}@1x.png`"
                width="28"
                height="20"
                alt=""
              />
              <span class="nl-prefix__code">+{{ currentCountryPrefix.prefix }}</span>
            </button>
            <input
              id="nl-login"
              v-model.trim="form.login"
              type="tel"
              name="tel"
              autocomplete="tel"
              class="nl-input nl-input--joined"
              required
            >
            <PrefixPicker
              :open="pickerOpen"
              :current-country-prefix="currentCountryPrefix"
              @select="onSelectPrefix"
              @close="closePicker"
            />
          </div>
          <input
            v-else
            id="nl-login"
            v-model.trim="form.login"
            type="text"
            :autocomplete="loginType === TYPE_EMAIL ? 'email' : 'username'"
            class="nl-input"
            required
          >
        </div>

        <!-- password -->
        <div class="nl-field">
          <label class="nl-field__label" for="nl-password">{{ $t('pages.newLogin.label.password') }}</label>
          <div class="nl-input nl-input--password">
            <input
              id="nl-password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              class="nl-input__control"
              required
            >
            <button
              type="button"
              class="nl-eye"
              :aria-label="$t('pages.newLogin.label.password')"
              @click="showPassword = !showPassword"
            >
              <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
            </button>
          </div>
        </div>

        <!-- remember me -->
        <b-form-checkbox v-if="!isApp && !isRiv" v-model="form.rememberMe" name="rememberMe" class="nl-remember">
          {{ $t('pages.newLogin.rememberMe') }}
        </b-form-checkbox>

        <!-- submit -->
        <button
          type="submit"
          class="nl-submit mt-5"
          :class="isRiv ? 'submit-btn' : 'nl-submit--brand'"
          :disabled="loading"
        >
          {{ $t('pages.newLogin.buttonOk') }}
        </button>

        <!-- forgot password -->
        <div class="nl-forgot">
          <router-link v-if="!isRiv" to="/recover" class="nl-forgot__link">
            {{ $t('pages.newLogin.forgot') }}
          </router-link>
          <a v-else role="button" class="nl-forgot__link" @click="showInfoMessage = true">
            {{ $t('pages.newLogin.forgot') }}
          </a>
        </div>
        <b-alert
          v-if="isRiv"
          :show="showInfoMessage"
          dismissible
          variant="info"
          class="mt-3"
          @dismissed="showInfoMessage = false"
        >
          <span class="nl-info" v-html="$t('pages.login.recoverPassReseller', { supportEmail })" />
        </b-alert>
      </b-form>

      <!-- register -->
      <div v-if="urlNewUser" class="nl-register">
        <span class="nl-register__text">{{ $t('pages.newLogin.noAccount') }}</span>
        <a :href="urlNewUser" class="nl-register__link" @click.prevent="handleNewUser">
          {{ $t('pages.newLogin.register') }}
        </a>
      </div>
    </div>
  </component>
</template>

<script>
// Login is the redesigned login page. The previous design is kept at OldLogin.vue
// (route /old-login) for side-by-side comparison; this component reuses the same
// services and utils rather than depending on OldLogin.vue.
// Note: its private i18n strings still live under the `pages.newLogin` namespace.
import LoginService from '@/services/LoginService'
import LoginSplitLayout from '@/components/LoginSplitLayout.vue'
import LoginSingleLayout from '@/components/LoginSingleLayout.vue'
import SegmentedTabs from '@/elements/SegmentedTabs.vue'
import PrefixPicker from '@/components/PrefixPicker.vue'
import { getProductName, buildSmartProvisioningUrl } from '@/utils/url'
import { trackEvent } from '@/utils/tracking'
import {
  E_SERVER_HTTP,
  E_CODE_UNAUTHORIZED,
  E_CODE_BADUSER,
  TYPE_PHONE,
  TYPE_EMAIL,
  SUPPORT_EMAIL_ADDR,
  TRACKING_ID_COOKIE_NAME
} from '@/constants'
import { SET_2FA_VALIDATION_SESSION, SET_2FA_PROXY } from '@/store/mutationTypes'
import { mapState } from 'vuex'
import { getFingerprintData } from '@/utils/fingerprint'
import { turnOffZoom } from '@/utils'
import { handlePostLoginResult } from '@/utils/login'
import nonceRefresh from '@/mixins/nonceRefresh'

// Visual-only third tab ("Codice utente"). Not yet wired to a backend.
const TYPE_CODE = 'code'

export default {
  name: 'Login',
  mixins: [nonceRefresh],
  components: {
    LoginSplitLayout,
    LoginSingleLayout,
    SegmentedTabs,
    PrefixPicker
  },
  data () {
    return {
      form: {
        login: '',
        password: '',
        fingerprint: {},
        rememberMe: false,
        origin: getProductName()
      },
      // match the Figma default selected tab: Cellulare for mnet/lyber, E-mail for riv
      loginType: getProductName() === 'riv' ? TYPE_EMAIL : TYPE_PHONE,
      asyncError: null,
      canClean: true,
      loading: false,
      showPassword: false,
      showInfoMessage: false,
      pickerOpen: false,
      supportEmail: SUPPORT_EMAIL_ADDR
    }
  },
  computed: {
    ...mapState(['currentCountryPrefix', 'apiNonce']),
    productName () {
      return getProductName()
    },
    isRiv () {
      return this.productName === 'riv'
    },
    isApp () {
      return this.productName === 'mtalk-app'
    },
    isPhoneForm () {
      return this.loginType === TYPE_PHONE
    },
    formData () {
      if (this.isPhoneForm) {
        return {
          ...this.form,
          login: `+${this.currentCountryPrefix.prefix + this.form.login}`
        }
      }
      return this.form
    },
    urlNewUser () {
      switch (this.productName) {
        case 'lyber':
          return process.env.VUE_APP_NEW_USER_LYBER
        case 'mnet':
          return process.env.VUE_APP_NEW_USER_MNET
        case 'smart':
        case 'snpro1':
        case 'snpro2':
        case 'snpro3':
          return buildSmartProvisioningUrl()
        case 'fax':
          return process.env.VUE_APP_NEW_USER_FAX
        default:
          return null
      }
    },
    tabs () {
      const phone = { text: this.$t('pages.newLogin.tabs.mobile'), value: TYPE_PHONE }
      const email = { text: this.$t('pages.newLogin.tabs.email'), value: TYPE_EMAIL }
      const code = { text: this.$t('pages.newLogin.tabs.code'), value: TYPE_CODE }
      return this.isRiv ? [] : [phone, email, code]
    },
    currentLabel () {
      if (this.isRiv) {
        return this.$t('pages.newLogin.label.username')
      } else {
        switch (this.loginType) {
          case TYPE_PHONE:
            return this.$t('pages.newLogin.label.phone')
          case TYPE_CODE:
            return this.$t('pages.newLogin.label.code')
          default:
            return this.$t('pages.newLogin.label.email')
        }
      }
    },
    layoutComponent () {
      return this.isRiv ? 'LoginSingleLayout' : 'LoginSplitLayout'
    },
    layoutProps () {
      if (this.isRiv) return {}
      return {
        heroImage: this.heroImage,
        heroAlt: '',
        logoSrc: this.logoSrc,
        logoAlt: this.logoAlt,
        logoWidth: this.logoWidth,
        hideLogo: this.productName === 'mtalk-app'
      }
    },
    heroImage () {
      return this.productName === 'lyber'
        ? '/img/login-hero-lyber.png'
        : '/img/login-hero-mnet.png'
    },
    logoSrc () {
      const logo = this.productName === 'lyber' ? 'lyber' : 'mnet'
      return require(`@/assets/logos/${logo}.png`)
    },
    logoWidth () {
      return this.productName === 'lyber' ? 118 : 184
    },
    logoAlt () {
      return this.productName === 'lyber' ? 'Lyber' : 'MessageNet'
    }
  },
  watch: {
    form: {
      handler () {
        if (this.canClean) {
          this.asyncError = null
        }
      },
      deep: true
    },
    loginType () {
      this.form.login = ''
    }
  },
  methods: {
    openPrefixPicker () {
      this.pickerOpen = !this.pickerOpen
    },
    onSelectPrefix (country) {
      this.$store.commit('SET_CURRENT_COUNTRY_PREFIX', country)
      this.closePicker('select')
    },
    closePicker (reason) {
      this.pickerOpen = false
      // return focus to the trigger on keyboard/selection close, but not when
      // the user dismissed the picker by clicking elsewhere
      if (reason !== 'outside') {
        this.$nextTick(() => {
          if (this.$refs.prefixButton) this.$refs.prefixButton.focus()
        })
      }
    },
    handleNewUser () {
      trackEvent('start_signup')
      window.location.href = this.urlNewUser
    },
    onSubmit () {
      this.asyncError = null
      this.loading = true
      return LoginService.mnetLogin(this.formData, this.apiNonce)
        .then(async (res) => {
          if (res['2fa'] && res['2fa'].validation_session) {
            this.$store.commit(SET_2FA_VALIDATION_SESSION, res['2fa'].validation_session)
            this.$store.commit(SET_2FA_PROXY, res['2fa'].proxy || null)
            window.location.assign('/two-factor-validation' + window.location.search)
            return
          }
          try {
            await handlePostLoginResult(res)
          } catch (error) {
            this.asyncError = error?.key ? { key: error.key, values: error.values } : { key: 'errors.unexpected' }
          }
        })
        .catch((err) => {
          this.loading = false
          if (err.id === E_SERVER_HTTP && err.code === E_CODE_UNAUTHORIZED) {
            if (this.loginType === TYPE_PHONE) {
              this.asyncError = { key: 'pages.login.errorLoginNumber' }
            } else if (this.loginType === TYPE_EMAIL) {
              this.asyncError = { key: 'pages.login.errorLoginEmail' }
            } else {
              this.asyncError = { key: 'pages.login.errorLoginCode' }
            }
          } else if (err.code === E_CODE_BADUSER) {
            this.asyncError = this.productName === 'lyber'
              ? { key: 'pages.login.errorBadUserLyber' }
              : { key: 'pages.login.errorBadUserMnet' }
          } else {
            this.asyncError = err?.key ? { key: err.key, values: err.values } : { key: 'errors.unexpected' }
            throw err
          }
        })
    }
  },
  async created () {
    this.TYPE_EMAIL = TYPE_EMAIL
    this.TRACKING_ID_COOKIE_NAME = TRACKING_ID_COOKIE_NAME
    // Default country prefix, previously seeded by PrefixChoiceLayout.
    // TODO: hand this off to the new prefix-picker component once added.
    if (!this.currentCountryPrefix || !this.currentCountryPrefix.prefix) {
      this.$store.commit('SET_CURRENT_COUNTRY_PREFIX', { code: 'IT', prefix: '39', name: 'Italia' })
    }
    this.form.fingerprint = await getFingerprintData()
  },
  mounted () {
    turnOffZoom()
  }
}
</script>

<style lang="scss" scoped>
.nl-form {
  width: 100%;

  &__tabs {
    margin-bottom: $spacer*1.5;
  }
}

.nl-remember {
  margin: $spacer*0.5 0 $spacer*3;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  color: $nl-text;
}

.nl-forgot {
  margin-top: $spacer;
  text-align: center;

  &__link {
    font-family: 'Source Sans Pro';
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 26px;
    color: $nl-text;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }
  }
}

.nl-register {
  margin-top: $spacer*4;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  line-height: 26px;

  &__text {
    color: $nl-text;
  }

  &__link {
    margin-left: 6px;
    font-weight: 600;
    color: $brand-safe;
    text-decoration: underline;
  }
}

.nl-info {
  font-family: 'Source Sans Pro';
  font-size: 0.9375rem;
}
</style>
